import {
  Button,
  Menu,
  MenuItem,
  MenuList,
  MenuPopover,
  MenuTrigger,
} from "@fluentui/react-components";
import {
  ArrowLeftRegular,
  ChevronDoubleLeftRegular,
  ChevronRightRegular,
  DocumentBulletListRegular,
  MoreHorizontalRegular,
  PlugConnectedRegular,
  PuzzlePieceRegular,
  ServerRegular,
  SettingsRegular,
} from "@fluentui/react-icons";
import classNames from "classnames";
import React from "react";
import { useNavigate } from "react-router";

import {
  AppRouteGroup,
  useAppRouteGroup,
  useAppRouteParams,
} from "@/AppRoutes";
import { translation } from "@/i18n";
import { nsSideNav } from "@/i18n/locales/sideNav";
import { buildOrgTopPath } from "@/utils/orgTopPath";
import { useAppAccount } from "../../AppProvider/AppAccountProvider";
import { useAppOrgsAndInvitedOrgs } from "../../AppProvider/AppOrgsAndInvitedOrgsProvider";
import { useCachedOrganization } from "../../InOrganizationProvider/OrganizationProvider";
import { usePermissions } from "../../InOrganizationProvider/PermissionsProvider";
import { OrganizationAvatar } from "../../OrganizationAvatar";
import { OrganizationMenu } from "../../OrganizationMenu";
import { SortableTmpMenuSet, TmpMenuSetDivider } from "../helpers";
import helperStyles from "../helpers/index.module.css";
import { useAppSideNavOpenState } from "../providers/AppSideNavOpenStateProvider";

import { AppSideNavNameTooltip } from "../AppSideNavNameTooltip";

import styles from "./index.module.css";

const HOVER_DELAY_MS = 100;

const useTranslation = translation.use(nsSideNav);

const ConnectSideNavMenuItem: React.FC<{
  orgPath: string;
  showConnect: boolean;
  showConnectMasters: boolean;
  active: boolean;
}> = ({ orgPath, showConnect, showConnectMasters, active }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  return (
    <Menu positioning="after-top" openOnHover hoverDelay={HOVER_DELAY_MS}>
      <MenuTrigger disableButtonEnhancement>
        <div
          role="button"
          tabIndex={0}
          className={classNames(helperStyles.menuNode, styles.connectRow, {
            [styles.connectRowActive]: active,
          })}
        >
          <div className={styles.connectRowIcon}>
            <PlugConnectedRegular />
          </div>
          <div className={helperStyles.menuNodeLeft}>
            <div className={helperStyles.text}>Connect</div>
          </div>
          <div className={styles.connectRowChevron}>
            <ChevronRightRegular />
          </div>
        </div>
      </MenuTrigger>
      <MenuPopover>
        <MenuList>
          {showConnectMasters ? (
            <MenuItem onClick={() => navigate(`${orgPath}/connect_masters`)}>
              {t.sideNav.item.connectMasters()}
            </MenuItem>
          ) : null}
          {showConnect ? (
            <MenuItem onClick={() => navigate(`${orgPath}/connects`)}>
              Connect
            </MenuItem>
          ) : null}
        </MenuList>
      </MenuPopover>
    </Menu>
  );
};

const EndpointAddonSideNavMenuItem: React.FC<{
  orgPath: string;
  showEndpointAddonMasters: boolean;
  showEndpointAddons: boolean;
  active: boolean;
}> = ({ orgPath, showEndpointAddonMasters, showEndpointAddons, active }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  return (
    <Menu positioning="after-top" openOnHover hoverDelay={HOVER_DELAY_MS}>
      <MenuTrigger disableButtonEnhancement>
        <div
          role="button"
          tabIndex={0}
          className={classNames(helperStyles.menuNode, styles.connectRow, {
            [styles.connectRowActive]: active,
          })}
        >
          <div className={styles.connectRowIcon}>
            <ServerRegular />
          </div>
          <div className={helperStyles.menuNodeLeft}>
            <div className={helperStyles.text}>Endpoint Addon</div>
          </div>
          <div className={styles.connectRowChevron}>
            <ChevronRightRegular />
          </div>
        </div>
      </MenuTrigger>
      <MenuPopover>
        <MenuList>
          {showEndpointAddonMasters ? (
            <MenuItem
              onClick={() => navigate(`${orgPath}/endpoint_addon_masters`)}
            >
              {t.sideNav.item.endpointAddonMasters()}
            </MenuItem>
          ) : null}
          {showEndpointAddons ? (
            <MenuItem onClick={() => navigate(`${orgPath}/endpoint_addons`)}>
              Endpoint Addon
            </MenuItem>
          ) : null}
        </MenuList>
      </MenuPopover>
    </Menu>
  );
};

const SETTINGS_SIDE_NAV_ROUTE_GROUPS: AppRouteGroup[] = [
  "organizationSetting",
  "algorithmVersion",
  "frontendAddon",
  "connect",
  "endpointAddon",
];

export const isSettingsSideNavRouteGroup = (
  routeGroup: AppRouteGroup | undefined
): boolean =>
  !!routeGroup && SETTINGS_SIDE_NAV_ROUTE_GROUPS.includes(routeGroup);

/** ヘッダーのみ表示モードで設定系ページに表示する固定サイドナビ。 */
export const AppSideNavSettings: React.FC = () => {
  const { t } = useTranslation();
  const routeGroup = useAppRouteGroup();
  const { organizationId } = useAppRouteParams();
  const { isAADeveloperOrAbove } = useAppAccount();
  const { hasPermission, isLoadedMe } = usePermissions();
  const { organization: memberOrganization } = useCachedOrganization();
  const { organizations: appOrganizations } = useAppOrgsAndInvitedOrgs();
  const organization =
    memberOrganization ??
    appOrganizations.find((org) => org.org_id === organizationId);

  // 再オープンはヘッダーの共通メニューボタン (PageHeader) が担うため、ここには展開ボタンを持たない。
  const { isGlobalOpen, setIsGlobalOpen, globalWidth } =
    useAppSideNavOpenState();

  const showAlgorithmVersions =
    !!organization && hasPermission("algorithm_versions", "read");
  const showFrontendAddons =
    !!organization &&
    (isAADeveloperOrAbove || hasPermission("frontend_addons_owned", "read"));
  const showConnectMasters =
    isAADeveloperOrAbove || hasPermission("connect_masters_owned", "read");
  // Endpoint Addonマスタは管理者 API のみで扱われるため aa:developer 以上に限定する。
  const showEndpointAddonMasters = isAADeveloperOrAbove;
  // Endpoint Addon (インスタンス) も管理者 API のみで扱われるため同様に限定する。
  const showEndpointAddons = isAADeveloperOrAbove;
  const showConnect = hasPermission("connects", "read");
  const showConnectAddon =
    !!organization && (showConnectMasters || showConnect);
  const showEndpointAddon =
    !!organization && (showEndpointAddonMasters || showEndpointAddons);
  const showAddonSection =
    showAlgorithmVersions ||
    showFrontendAddons ||
    showConnectAddon ||
    showEndpointAddon;

  const orgPath = `/organizations/${organizationId}`;

  return (
    <aside
      role="navigation"
      aria-label={t.sideNav.settings.ariaLabel()}
      aria-hidden={!isGlobalOpen}
      className={classNames(styles.root, { [styles.isClosed]: !isGlobalOpen })}
      style={{ width: `${isGlobalOpen ? globalWidth : 0}px` }}
    >
      <div className={styles.firstMenu}>
        <OrganizationMenu>
          <div className={styles.content} role="button" tabIndex={0}>
            <OrganizationAvatar name={organization?.name ?? ""} size={24} />
            <div className={styles.textWithTooltip}>
              <AppSideNavNameTooltip
                content={organization?.name ?? ""}
                relationship="description"
              >
                <div className={styles.title}>{organization?.name}</div>
              </AppSideNavNameTooltip>
            </div>
            <div className={styles.rightIcon}>
              <MoreHorizontalRegular />
            </div>
          </div>
        </OrganizationMenu>
      </div>
      <TmpMenuSetDivider
        className={classNames(styles.divider, styles.firstDivider)}
      />
      <div className={styles.secondRoot}>
        <SortableTmpMenuSet
          id="organization_setting"
          text={t.sideNav.item.organizationSetting()}
          to={`${orgPath}/organization_setting/organization_info`}
          iconElement={<SettingsRegular />}
          active={routeGroup === "organizationSetting"}
          isDisableTooltip
        />
        {isLoadedMe && showAddonSection ? (
          <>
            <TmpMenuSetDivider className={styles.divider} />
            {showAlgorithmVersions ? (
              <SortableTmpMenuSet
                id="algorithm_versions"
                text={t.sideNav.item.algorithmVersions()}
                to={`${orgPath}/algorithm_versions`}
                iconElement={<DocumentBulletListRegular />}
                active={routeGroup === "algorithmVersion"}
                isDisableTooltip
              />
            ) : null}
            {showFrontendAddons ? (
              <SortableTmpMenuSet
                id="frontend_addons"
                text={t.sideNav.item.frontendAddons()}
                to={`${orgPath}/frontend_addons`}
                iconElement={<PuzzlePieceRegular />}
                active={routeGroup === "frontendAddon"}
                isDisableTooltip
              />
            ) : null}
            {showConnectAddon ? (
              <ConnectSideNavMenuItem
                orgPath={orgPath}
                showConnect={showConnect}
                showConnectMasters={showConnectMasters}
                active={routeGroup === "connect"}
              />
            ) : null}
            {showEndpointAddon ? (
              <EndpointAddonSideNavMenuItem
                orgPath={orgPath}
                showEndpointAddonMasters={showEndpointAddonMasters}
                showEndpointAddons={showEndpointAddons}
                active={routeGroup === "endpointAddon"}
              />
            ) : null}
          </>
        ) : null}
        <TmpMenuSetDivider className={styles.divider} />
        <SortableTmpMenuSet
          id="back_to_organization"
          text={t.sideNav.item.back()}
          to={buildOrgTopPath(
            organizationId,
            organization?.initial_extra_page_id
          )}
          iconElement={<ArrowLeftRegular />}
          isDisableTooltip
        />
      </div>
      <TmpMenuSetDivider />
      <div className={styles.thirdRoot}>
        <Button
          appearance="subtle"
          icon={<ChevronDoubleLeftRegular />}
          aria-label={t.sideNav.settings.closeButtonAriaLabel()}
          className={classNames(styles.footerButton, styles.footerButtonExpand)}
          onClick={() => setIsGlobalOpen(false)}
        />
      </div>
    </aside>
  );
};
